import React from "react";
import { motion } from "motion/react";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import fallbackCover from "@images/logo-share-v2.webp";

// `dense` = the /blogs listing grid, which shows two cards per row on
// mobile; below `sm` the card drops the excerpt and meta row and shrinks
// type/padding. From `sm` up it is identical to the default card.
const BlogCard = ({ blog, index, dense = false }) => {
    const href = `/blogs/${blog.seo_data?.slug || blog.id}`;
    const d = (compact, full) => (dense ? compact : full);

    return (
        <motion.article
            className="group relative flex flex-col overflow-hidden rounded-[18px] bg-white shadow-[0_4px_20px_0_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_16px_40px_-8px_rgba(37,99,235,0.25)]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: [0.25, 0.1, 0.25, 1],
            }}
        >
            {/* Thumbnails are already branded graphics (logo + headline baked
                in by the cover pipeline), so this card doesn't overlay a
                second category pill on top of the image — it would clash
                with the artwork. Category shows once, below, as real text. */}
            <a href={href} className="relative block overflow-hidden">
                <div className="relative aspect-video overflow-hidden bg-gray-100">
                    <img
                        src={blog.thumbnail}
                        alt={blog.thumbnail_alt || blog.title}
                        width={800}
                        height={450}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                        // A handful of published posts have a thumbnail path
                        // in the DB whose file no longer exists on disk (the
                        // upload never persisted) — rather than a blank box,
                        // fall back once to the real site logo so the card
                        // still reads as intentional. Real cover images for
                        // those specific posts still need re-uploading via
                        // the dashboard; this is just graceful degradation,
                        // not a substitute for the real fix.
                        onError={(e) => {
                            if (e.currentTarget.src !== fallbackCover) {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = fallbackCover;
                                e.currentTarget.className = "h-full w-full object-contain p-10 bg-gray-50";
                            }
                        }}
                    />
                </div>
            </a>

            <div className={`flex flex-1 flex-col ${d("p-3 sm:p-6", "p-6")}`}>
                <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 ${d("mb-2 sm:mb-3", "mb-3")}`}>
                    <span className={`rounded-full bg-primary-50 text-primary-700 font-bold py-1 ${d("text-[10px] sm:text-[11px] px-2 sm:px-2.5", "text-[11px] px-2.5")}`}>
                        {blog.category}
                    </span>
                    <span className={`${d("hidden sm:flex", "flex")} items-center gap-1.5 text-[11px] text-gray-400 font-medium`}>
                        <Calendar className="h-3.5 w-3.5" />
                        <time dateTime={blog.date}>{blog.date}</time>
                    </span>
                    {blog.reading_time != null && (
                        <span className={`${d("hidden sm:flex", "flex")} items-center gap-1.5 text-[11px] text-gray-400 font-medium`}>
                            <Clock className="h-3.5 w-3.5" />
                            {blog.reading_time} دقائق قراءة
                        </span>
                    )}
                </div>

                <a href={href}>
                    <h3 className={`${d("mb-3 sm:mb-2.5 text-sm sm:text-2xl", "mb-2.5 text-2xl")} font-bold leading-snug text-gray-900 transition-colors group-hover:text-primary-600 line-clamp-2`}>
                        {blog.title}
                    </h3>
                </a>

                {/* Wrapper carries the mobile hide: `sm:block` on the <p> itself would
                    override line-clamp's -webkit-box display. */}
                <div className={dense ? "hidden sm:block" : undefined}>
                    <p className="mb-5 text-base leading-relaxed text-gray-500 line-clamp-3">
                        {blog.description}
                    </p>
                </div>

                <a
                    href={href}
                    className={`mt-auto flex items-center justify-center rounded-full bg-primary-600 hover:bg-primary-700 text-white font-semibold transition-colors ${d("min-h-11 gap-1.5 sm:gap-2 text-xs sm:text-base py-2 sm:py-3", "gap-2 text-base py-3")}`}
                >
                    عرض المزيد
                    <ArrowLeft className={d("h-3.5 w-3.5 sm:h-4 sm:w-4", "h-4 w-4")} />
                </a>
            </div>
        </motion.article>
    );
};

export default BlogCard;
